import React, { useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import Modal from "../../../../../components/modal";
import ConfirmationModal from "../../../../application/components/ConfirmationModal/ConfirmationModal";
import TextInput from "../../../../../components/form/text-input";
import Select from "../../../../../components/form/select";
import Button from "../../../../../components/new-button";
import SlidingRadioInput from "../../../../../components/form/sliding-radio-input";
import formBaseStyles from "./../../../../../components/form/text-input/styles.module.scss";
import styles from "./styles.module.scss";
import useUserStore from "../../../../../stores/user";
import useOrganizationStore from "../../../../../stores/organization";
import TagInput from "../../../../../components/form/tag-input";
import { filterAutoSubmitProcessors } from "../../../../../utils/auto-submit-processors";
import { AddMemberToOrganization, UpdateMember, getSubOrganizations } from "../../../../../../api";
import toast from "react-hot-toast";
import { flattenValidationErrors } from "../../../../../../components/helper/helper";
import config from "../../../../../../config";

const normalizeOfficeTags = (offices) => {
  if (!Array.isArray(offices)) {
    return [];
  }

  return offices
    .map((office) => {
      if (typeof office === "string" || typeof office === "number") {
        const value = String(office);
        return { label: value, value };
      }

      if (office && typeof office === "object") {
        const label = office.label ?? office.value;
        const value = office.value ?? office.label;

        if (label && value) {
          return {
            label: String(label),
            value: String(value),
          };
        }
      }

      return null;
    })
    .filter(Boolean);
};

const getSchema = (isKurvPayOrganization, isEntitySuperAdmin) => yup.object({
  first_name: yup.string().trim().required("* First Name is required"),
  last_name: yup.string().trim().required("* Last Name is required"),
  email: yup.string().email().required("* Email required"),
  role: yup.string().required("* Role required"),
  channel: isEntitySuperAdmin
    ? yup.string().transform((value) => value == 'false' ? '' : value).required("* Channel required")
    : yup.string().transform((value) => value == 'false' ? '' : value).nullable(),
  team: yup.string().transform((value) => value == 'false' ? '' : value).nullable(),
  ems_entity: isEntitySuperAdmin
    ? yup.string().transform((value) => value == 'false' ? '' : value).required("* EMS Entity required")
    : yup.string().transform((value) => value == 'false' ? '' : value).nullable(),
  split: yup.number().nullable(),
  override_split: yup.string().transform(value => value == 'false' ? 0 : value).when('is_override', {
    is: 1,
    then: yup.string().required("* Override Split required"),
    otherwise: yup.string().notRequired(),
  }),
  override_department_id: yup.string().transform(value => value == 'false' ? 0 : value).when('is_override', {
    is: 1,
    then: yup.string().nullable().required("* Override Department required"),
    otherwise: yup.string().nullable(),
  }),
  override_start_date: yup.string().transform(value => value == null ? "" : value).when('is_override', {
    is: 1,
    then: yup.string().required("* Override Start string required"),
    otherwise: yup.string().notRequired(),
  }),
  low_risk_payout_percentage: yup.number().max(50, 'Low risk payout % must be less than or equal to 50').nullable(),
  high_risk_payout_percentage: yup.number().max(50, 'High risk payout % must be less than or equal to 50').nullable(),
  ems_office: isEntitySuperAdmin
    ? yup.array().min(1, "* At least one Kurv Office is required")
    : yup.array().nullable(),
});

const AddMemberModal = ({ open, onClose, isEdit = false, selectedMember, subOrgId = 0 }) => {
  const [errors, setErrors] = useState({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const formikRef = useRef(null);
  const isAdmin = useUserStore((state) => state.isAdmin);
  const isEntitySuperAdmin = useUserStore((state) => state.isEntitySuperAdmin);
  const isPCAdmin = useUserStore((state) => state.isPCAdmin);
  const isEMSPCAdmin = useUserStore((state) => state.isEMSPCAdmin);
  const isEditAndisPCAdmin = isEdit && isPCAdmin;
  const user = useUserStore((state) => state.user);

  const isEditAndisEMSPCAdmin = isEdit && isEMSPCAdmin;
  const emsOffices = useOrganizationStore((state) => state.emsOffices);
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationId,
  );
  const selectedOrganizationData = useOrganizationStore(
    (state) => state.selectedOrganizationData,
  );
  const isKurvPayOrganization = ["kurvpay", "ems"].includes(
    selectedOrganizationData?.organization?.ems_entity,
  );
  const subOrgs = useOrganizationStore((state) => state.subOrgs);
  const setSubOrgs = useOrganizationStore((state) => state.setSubOrgs);
  const zohoUsers = useOrganizationStore((state) => state.zohoUsers);
  const departments = useOrganizationStore((state) => state.departments);
  const isKurvEntityAdmin = useUserStore((state) => state.isKurvPayEntity) && isAdmin;
  const isGodUser = useUserStore((state) => state.isGodUser);
  // A member's verified cell number is their login second factor. The server only
  // accepts a change to it from the member themselves (by OTP) or from a god user,
  // whose edit resets verification — that is the way back in for a member who can
  // no longer receive the code.
  const memberPhoneVerified = !!selectedMember?.phone_cell_otp_verified;
  const memberPhoneLocked = memberPhoneVerified && !isGodUser;
  const isEditAndKurvEntityAdmin = isEdit && isKurvEntityAdmin;
  // Only the banks with an auto-submit integration — the rest of the org's
  // bank_applications would be silently dropped by the backend.
  const bankApplicationOptions = filterAutoSubmitProcessors(
    selectedOrganizationData?.organization?.bank_applications,
  );

  const formData = {
    first_name: selectedMember ? selectedMember.name.split(" ")[0] : "",
    last_name: selectedMember ? selectedMember.name.split(" ")[1] : "",
    email: selectedMember ? selectedMember.email : "",
    role: selectedMember ? selectedMember.role : "",
    office_id: selectedMember ? selectedMember.office_id : "",
    branch_id: selectedMember ? selectedMember.branch_id : "",
    phone_direct: selectedMember ? selectedMember.phone_direct : "",
    phone_cell: selectedMember ? selectedMember.phone_cell : "",
    address: selectedMember ? selectedMember.address : "",
    zoho_user_id: selectedMember ? selectedMember.zoho_user_id : "",
    is_view_residual: selectedMember ? selectedMember.is_view_residual : false,
    is_override: selectedMember ? selectedMember.is_override : false,
    override_department_id: selectedMember
      ? selectedMember.override_department_id
      : false,
    is_god_user: selectedMember ? selectedMember.is_god_user : null,
    override_split: selectedMember ? selectedMember.override_split : false,
    split: selectedMember == null ? 0 : selectedMember.split,
    channel: selectedMember ? selectedMember.channel : false,
    team: selectedMember ? selectedMember.team : false,
    sub_org: selectedMember ? selectedMember.sub_org_id : (subOrgId && subOrgId !== 0 ? subOrgId : null),
    sub_org_id: selectedMember ? selectedMember.sub_org_id : (subOrgId && subOrgId !== 0 ? subOrgId : null),
    ems_branch: selectedMember ? selectedMember.ems_branch : null,
    ems_office: selectedMember ? normalizeOfficeTags(selectedMember.ems_office) : [],
    ems_rep_id: selectedMember ? selectedMember.ems_rep_id : null,
    host_signing: selectedMember ? selectedMember.host_signing : null,
    ems_entity: selectedMember ? selectedMember.ems_entity : "pc",
    beta_features: selectedMember
      ? selectedMember?.beta_features == 1
        ? 1
        : false
      : false,
    auto_submit_to_banks:
      selectedMember?.auto_submit_to_banks || [],
    bypass_nab: selectedMember ? selectedMember?.bypass_nab : 0,
    bypass_ems: selectedMember ? selectedMember?.bypass_ems : 0,
    is_notifications_enabled: selectedMember ? (selectedMember?.is_notifications_enabled == 1 ? 1 : 0) : 0,
    override_start_date: selectedMember ? selectedMember.override_start_date : null,
    is_v2_enable: selectedMember ? selectedMember.is_v2_enable : 0,
    low_risk_payout_percentage: selectedMember ? selectedMember.low_risk_payout_percentage : null,
    high_risk_payout_percentage: selectedMember ? selectedMember.high_risk_payout_percentage : null,
  };


  const handleMemberFormSubmit = async (values) => {
    const toastId = toast.loading(isEdit ? "Updating member..." : "Adding member...");

    try {
      let response;

      if (isEdit) {
        values.id = selectedMember.id;
        response = await UpdateMember(selectedOrganizationId, values);
      } else {
        response = await AddMemberToOrganization(selectedOrganizationId, values);
      }

      if (response.success) {
        toast.success(
          isEdit ? "Member updated successfully" : "Member added successfully",
          { id: toastId, duration: 5000 }
        );
        if (response?.subAgentAttempted && !response?.subAgentStatus) {
          const actionLabel = isEdit ? "Update" : "Creation";
          toast.error(`SubAgent ${actionLabel} API request failed: ${response.subAgentResponseMessage}`, { icon: "🚨", duration: 30000 });
        }
        onClose();
      } else {
        const flattenedErrors = flattenValidationErrors(response.errors);
        setErrors(flattenedErrors);
        toast.error(
          isEdit ? "Failed to update member" : "Failed to add member",
          { id: toastId, duration: 5000 }
        );
      }
    } catch (error) {
      toast.error(
        isEdit ? "Failed to update member" : "Failed to add member",
        { id: toastId, duration: 5000 }
      );
    }
  };

  const handleBackdropClose = () => {
    if (formikRef.current?.dirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  useEffect(() => {
    const fetchSubOrgs = async () => {
      try {
        const response = await getSubOrganizations(selectedOrganizationId, { all: true });
        if (response.status) {
          setSubOrgs(response.data);
        }
      } catch (error) {
        console.error("Error fetching sub organizations:", error);
        toast.error("Failed to load sub-organizations", { duration: 5000 });
      }
    };
    fetchSubOrgs();
  }, [selectedOrganizationId]);

  return (
    <>
      <Modal
        isOpen={open}
        onClose={onClose}
        confirmClose={handleBackdropClose}
        header={`${isEdit ? "Edit" : "Add"} member`}
        notCentered
        size="md"
      >
        <Formik
          innerRef={formikRef}
          enableReinitialize
          validationSchema={getSchema(isKurvPayOrganization, isEntitySuperAdmin)}
          onSubmit={handleMemberFormSubmit}
          initialValues={formData}
          initialErrors={errors}
          validateOnBlur={false}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            setFieldValue,
            isSubmitting,
          }) => (
            <form autoComplete="off" onSubmit={handleSubmit}>
              <div className={styles.formFields}>
                <div className={`${styles.formRow} ${styles.formRow3}`}>
                  <TextInput
                    label="First name"
                    id="first_name"
                    name="first_name"
                    defaultValue={values.first_name}
                    onChange={handleChange}
                    touched={touched.first_name}
                    errored={errors.first_name}
                    error={errors["first_name"]}
                  />
                  <TextInput
                    label="Last name"
                    id="last_name"
                    name="last_name"
                    defaultValue={values.last_name}
                    onChange={handleChange}
                    touched={touched.last_name}
                    errored={errors.last_name}
                    error={errors["last_name"]}
                  />
                  <Select
                    label="Role"
                    placeholder="Select role"
                    id="role"
                    name="role"
                    options={[
                      { value: "agent", label: "Agent" },
                      { value: "admin", label: "Admin" },
                    ]}
                    value={values?.role}
                    onChange={(value) => {
                      setFieldValue("role", value);
                    }}
                    errored={errors.role}
                    touched={touched.role}
                    error={errors["role"]}
                  />
                  <TextInput
                    label="Direct phone"
                    id="phone_direct"
                    name="phone_direct"
                    defaultValue={values.phone_direct}
                    // onChange={handleChange}
                    touched={touched.phone_direct}
                    errored={errors.phone_direct}
                    error={errors["phone_direct"]}
                    cleaveOptions={{
                      blocks: [3, 3, 4],
                      delimiter: "-",
                      numericOnly: true,
                      onValueChanged: (e) => {
                        setFieldValue("phone_direct", e.target.value);
                      },
                    }}
                  />
                  <TextInput
                    label="Cell phone"
                    id="phone_cell"
                    name="phone_cell"
                    defaultValue={values.phone_cell}
                    // onChange={handleChange}
                    touched={touched.phone_cell}
                    errored={errors.phone_cell}
                    error={errors["phone_cell"]}
                    disabled={memberPhoneLocked}
                    hasWarning={
                      memberPhoneLocked
                        ? "Verified number — only this member can change it, by SMS code."
                        : memberPhoneVerified
                          ? "Verified number. Changing or clearing it here resets their verification."
                          : ""
                    }
                    // A verified number is stored in E.164 (+12135550123), which the
                    // mask below would silently truncate, so it is edited as plain
                    // text instead.
                    onChange={
                      memberPhoneVerified
                        ? (e) => setFieldValue("phone_cell", e.target.value)
                        : undefined
                    }
                    cleaveOptions={
                      memberPhoneVerified
                        ? undefined
                        : {
                            blocks: [3, 3, 4],
                            delimiter: "-",
                            numericOnly: true,
                            onValueChanged: (e) => {
                              setFieldValue("phone_cell", e.target.value);
                            },
                          }
                    }
                  />
                  <TextInput
                    label="Business email"
                    id="email"
                    name="email"
                    defaultValue={values.email}
                    onChange={handleChange}
                    touched={touched.email}
                    errored={errors.email}
                    error={errors["email"]}
                  />
                </div>

                <div className={`${styles.formRow} ${styles.formRow1}`}>
                  <TextInput
                    label="Address"
                    id="address"
                    name="address"
                    defaultValue={values.address}
                    onChange={handleChange}
                    touched={touched.address}
                    errored={errors.address}
                    error={errors["address"]}
                  />
                </div>

                {isEntitySuperAdmin && (
                <div className={`${styles.formRow} ${styles.formRow3}`}>
                  <Select
                    label="Channel"
                    placeholder="Select channel"
                    placeholderAsBlankOption
                    id="channel"
                    name="channel"
                    options={[
                      { value: "partner_channel", label: "Partner Channel" },
                      { value: "sales_channel", label: "Sales Channel" },
                      { value: "isv_channel", label: "ISV Channel" },
                    ]}
                    value={values?.channel}
                    onChange={(value) => {
                      setFieldValue("channel", value);
                    }}
                    errored={errors.channel}
                    touched={touched.channel}
                    error={errors["channel"]}
                  />
                  <Select
                    label="Team"
                    placeholder="Select team"
                    placeholderAsBlankOption
                    id="team"
                    name="team"
                    options={[
                      {
                        value: "business_development_executive",
                        label: "Business Development Executive",
                      },
                      {
                        value: "relationship_manager",
                        label: "Relationship Manager",
                      },
                    ]}
                    value={values?.team}
                    onChange={(value) => {
                      setFieldValue("team", value);
                    }}
                    errored={errors.team}
                    error={errors["team"]}
                  />
                  <Select
                    label="EMS entity"
                    placeholder="Select entity"
                    id="ems_entity"
                    name="ems_entity"
                    options={[
                      { label: "PC", value: "pc" },
                      { label: "EMS", value: "ems" },
                      { label: "PaymentNerds", value: "paymentnerds" },
                      { label: "Paychex", value: "paychex" },
                    ]}
                    value={values?.ems_entity}
                    onChange={(value) => {
                      setFieldValue("ems_entity", value);
                    }}
                    errored={errors.ems_entity}
                    error={errors["ems_entity"]}
                  />
                </div>
                )}

                {isEntitySuperAdmin && (
                <div className={`${styles.formRow} ${styles.formRow3} ${styles.formRowGap1}`}>
                  <div className={styles.radioGroup}>
                    <label className={`${formBaseStyles.labelLarge}`}>
                      View organization Residuals?
                    </label>
                    <SlidingRadioInput
                      onChange={(value) => {
                        setFieldValue("is_view_residual", value);
                      }}
                      selected={values?.is_view_residual || 0}
                      options={[
                        { label: "Yes", value: 1 },
                        { label: "No", value: 0 },
                      ]}
                    />
                  </div>
                  {isEdit && (
                    <>
                      <div className={styles.radioGroup}>
                        <label className={`${formBaseStyles.labelLarge}`}>
                          Host signing?
                        </label>
                        <SlidingRadioInput
                          selected={values?.host_signing}
                          onChange={(value) => {
                            setFieldValue("host_signing", value);
                          }}
                          options={[
                            { label: "Yes", value: 1 },
                            { label: "No", value: 0 },
                          ]}
                        />
                      </div>
                      <div className={styles.radioGroup}>
                        <label className={`${formBaseStyles.labelLarge}`}>
                          God User
                        </label>
                        <SlidingRadioInput
                          selected={values?.is_god_user}
                          onChange={(value) => {
                            setFieldValue("is_god_user", value);
                          }}
                          options={[
                            { label: "Yes", value: 1 },
                            { label: "No", value: 0 },
                          ]}
                        />
                      </div>
                    </>
                  )}
                </div>
                )}

                {(isEditAndisPCAdmin || isEditAndKurvEntityAdmin) && (
                  <div className={`${styles.formRow} ${styles.formRow3} ${styles.formRowGap1}`}>
                    {isEntitySuperAdmin && (
                    <div className={styles.radioGroup}>
                      <label className={`${formBaseStyles.labelLarge}`}>
                        Beta Features
                      </label>
                      <SlidingRadioInput
                        onChange={(value) => {
                          setFieldValue("beta_features", value);
                        }}
                        selected={values?.beta_features || 0}
                        options={[
                          { label: "Yes", value: 1 },
                          { label: "No", value: 0 },
                        ]}
                      />
                    </div>
                    )}

                    <div className={styles.radioGroup}>
                      <label className={`${formBaseStyles.labelLarge}`}>
                        Notifications Enabled
                      </label>
                      <SlidingRadioInput
                        selected={values?.is_notifications_enabled || 0}
                        onChange={(value) => {
                          setFieldValue("is_notifications_enabled", value);
                        }}
                        options={[
                          { label: "Yes", value: 1 },
                          { label: "No", value: 0 },
                        ]}
                      />
                    </div>

                    <TagInput
                      label="Auto Submit to Banks Applications"
                      id="auto_submit_to_banks"
                      name="auto_submit_to_banks"
                      placeholder={
                        bankApplicationOptions?.length
                          ? "Search bank applications"
                          : "No bank applications available"
                      }
                      options={bankApplicationOptions}
                      value={values?.auto_submit_to_banks || []}
                      onChange={(value) => {
                        setFieldValue("auto_submit_to_banks", value);
                      }}
                    />
                  </div>
                )}

                {isEdit && selectedOrganizationId == 1 && (
                  <div className={`${styles.formRow} ${styles.formRow3}`}>
                    <Select
                      label="Zoho User"
                      id="zoho_user_id"
                      name="zoho_user_id"
                      options={zohoUsers}
                      value={values?.zoho_user_id}
                      placeholder={!zohoUsers?.length ? "No Zoho users available" : "Select Zoho user"}
                    />
                  </div>
                )}

                {isEntitySuperAdmin && (
                <div className={`${styles.formRow} ${styles.formRow3}`}>
                  <TextInput
                    label="Split"
                    id="split"
                    name="split"
                    value={values.split}
                    type="text"
                    onChange={handleChange}
                    touched={touched.split}
                    errored={errors.split}
                    error={errors["split"]}
                  />
                </div>
                )}

                {isEdit && user?.id && isKurvEntityAdmin && (
                  <>
                    <div className={`${styles.formRow} ${styles.formRow3} ${styles.formRowGap1}`}>
                      <div className={styles.radioGroup}>
                        <label className={`${formBaseStyles.labelLarge}`}>
                          Is Override?
                        </label>
                        <SlidingRadioInput
                          selected={values?.is_override || 0}
                          onChange={(value) => {
                            setFieldValue("is_override", value);
                          }}
                          options={[
                            { label: "Yes", value: 1 },
                            { label: "No", value: 0 },
                          ]}
                        />
                      </div>
                        <div className={styles.radioGroup}>
                          <label className={`${formBaseStyles.labelLarge}`}>
                            Is V2 Enable?
                          </label>
                          <SlidingRadioInput
                            selected={values?.is_v2_enable || 0}
                            onChange={(value) => {
                              setFieldValue("is_v2_enable", value);
                            }}
                            options={[
                              { label: "Yes", value: 1 },
                              { label: "No", value: 0 },
                            ]}
                          />
                        </div>
                    </div>

                    {values?.is_override == 1 && (
                      <div className={`${styles.formRow} ${styles.formRow3}`}>
                        <TextInput
                          label="Override Split"
                          id="override_split"
                          name="override_split"
                          defaultValue={values.override_split}
                          onChange={handleChange}
                          touched={touched.override_split}
                          errored={errors.override_split}
                          error={errors["override_split"]}
                          type="number"
                        />

                        <Select
                          label="Override Department"
                          placeholder="Select department"
                          id="override_department_id"
                          name="override_department_id"
                          options={departments}
                          value={values?.override_department_id}
                          onChange={(value) => {
                            setFieldValue("override_department_id", value);
                          }}
                          errored={errors.override_department_id}
                          error={errors["override_department_id"]}
                        />

                        <TextInput
                          label="Override Start Date"
                          id="override_start_date"
                          name="override_start_date"
                          defaultValue={values.override_start_date}
                          type="text"
                          onChange={handleChange}
                          touched={touched.override_start_date}
                          errored={errors.override_start_date}
                          error={errors["override_start_date"]}
                          cleaveOptions={{
                            date: true,
                            delimiter: '-',
                            datePattern: ['Y', 'm', 'd'],
                            onValueChanged: (e) => {
                              setFieldValue("override_start_date", e.target.value);
                            },
                          }}
                        />
                      </div>
                    )}
                  </>
                )}


                {/* Show EMS Office input if user is PC Admin and is EMS Admin and is editing a member */}
                {isEntitySuperAdmin && (
                <div className={`${styles.formRow} ${styles.formRow2}`}>
                  <TextInput
                    label="Kurv Branch"
                    id="ems_branch"
                    name="ems_branch"
                    defaultValue={values.ems_branch}
                    onChange={handleChange}
                    touched={touched.ems_branch}
                    errored={errors.ems_branch}
                    error={errors["ems_branch"]}
                    cleaveOptions={{
                      blocks: [4],
                      numericOnly: true,
                      onValueChanged: (e) => {
                        setFieldValue("ems_branch", e.target.value);
                      },
                    }}
                  />

                  <TextInput
                    label="Kurv Rep Id"
                    id="ems_rep_id"
                    name="ems_rep_id"
                    defaultValue={values.ems_rep_id}
                    onChange={handleChange}
                    touched={touched.ems_rep_id}
                    errored={errors.ems_rep_id}
                    error={errors["ems_rep_id"]}
                    cleaveOptions={{
                      blocks: [4],
                      numericOnly: true,
                      onValueChanged: (e) => {
                        setFieldValue("ems_rep_id", e.target.value);
                      },
                    }}
                  />
                </div>
                )}

                {/* Show EMS Office input if user is PC Admin and is EMS Admin and is editing a member */}
                {isEntitySuperAdmin && (
                <div className={`${styles.formRow} ${styles.formRow1} ${styles.formRowGap1}`}>
                  <TagInput
                    label="Kurv Office"
                    id="ems_office"
                    name="ems_office"
                    options={emsOffices}
                    value={values?.ems_office}
                    onChange={(value) => {
                      setFieldValue("ems_office", value);
                    }}
                    search={true}
                  />
                  <p className={styles.error}>{errors.ems_office}</p>
                </div>
                )}

                {/* Show EMS Department input if user is PC Admin and is EMS Admin and is editing a member */}
                {isEntitySuperAdmin && (
                  <div className={`${styles.formRow} ${styles.formRow2}`}>
                    <TextInput
                      label="Low Risk Payout Percentage"
                      id="low_risk_payout_percentage"
                      name="low_risk_payout_percentage"
                      defaultValue={values.low_risk_payout_percentage}
                      onChange={handleChange}
                      touched={errors.low_risk_payout_percentage}
                      errored={errors.low_risk_payout_percentage}
                      error={errors["low_risk_payout_percentage"]}
                    />

                    <TextInput
                      label="High Risk Payout Percentage"
                      id="high_risk_payout_percentage"
                      name="high_risk_payout_percentage"
                      defaultValue={values.high_risk_payout_percentage}
                      onChange={handleChange}
                      touched={errors.high_risk_payout_percentage}
                      errored={errors.high_risk_payout_percentage}
                      error={errors["high_risk_payout_percentage"]}
                    />
                  </div>
                )}

                {isEntitySuperAdmin && subOrgs?.length > 0 && (
                  <div className={`${styles.formRow} ${styles.formRow1} ${styles.formRowGap1}`}>
                    <Select
                      label="Sub Organization"
                      id="sub_org"
                      name="sub_org"
                      placeholder="Select sub organization"
                      placeholderAsBlankOption
                      options={subOrgs.map((subOrg) => ({
                        label: subOrg.sub_org_name,
                        value: subOrg.id,
                      }))}
                      value={values?.sub_org}
                      onChange={(value) => {
                        setFieldValue("sub_org", value.value);
                      }}
                    />
                  </div>
                )}
              </div>

              <div className={styles.formActions}>
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Save
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </Modal>
      <ConfirmationModal
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirmClick={handleDiscardConfirm}
        message="You have unsaved changes. Discard them?"
        submitButtonText="Discard"
      />
    </>
  );
};

export default AddMemberModal;
